import { isUndefined } from 'lodash';
import {
  FontStyle,
  FontWeight,
  ReportElementBaseOption,
  ReportElementIconDesignOptions,
  ReportElementImageDesignOptions,
  ReportElementImageSupportedFillMode,
  ReportElementShapeDesignOptions,
  ReportElementTextDesignOption,
  TextDecoration,
} from '@/modules/ta/reportstudio/reportstudio.constants';
import { Position } from '@/modules/core/charts/chart.constants';
import { changeColorOpacity } from '@/modules/core/app/utils/ColorUtil';

export class ReportStudioElementStylesUIFactory {
  static shouldApplyShadowColor(baseOptions) {
    return (
      !isUndefined(baseOptions[ReportElementBaseOption.SHADOW_X]) &&
      !isUndefined(baseOptions[ReportElementBaseOption.SHADOW_Y]) &&
      !isUndefined(baseOptions[ReportElementBaseOption.SHADOW_BLUR]) &&
      baseOptions[ReportElementBaseOption.SHADOW_COLOR]
    );
  }

  static shouldApplyBorderColor(baseOptions) {
    return (
      baseOptions[ReportElementBaseOption.BORDER_COLOR] &&
      baseOptions[ReportElementBaseOption.BORDER_WIDTH] &&
      baseOptions[ReportElementBaseOption.BORDER_TYPE] &&
      baseOptions[ReportElementBaseOption.BORDER_OPACITY]
    );
  }

  static getTextElementStyles(element) {
    const designOptions = element.metadata.design_options;
    const styles = {};

    if (designOptions[ReportElementTextDesignOption.VERTICAL_ALIGNMENT]) {
      styles['justify-content'] = designOptions[ReportElementTextDesignOption.VERTICAL_ALIGNMENT];
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_OPACITY] >= 0) {
      styles.opacity = designOptions[ReportElementTextDesignOption.TEXT_OPACITY] / 100;
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_PADDING]) {
      styles.padding = `${designOptions[ReportElementTextDesignOption.TEXT_PADDING]}px`;
    }

    if (designOptions[ReportElementTextDesignOption.FONT_SIZE]) {
      styles['font-size'] = `${designOptions[ReportElementTextDesignOption.FONT_SIZE]}px`;
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_COLOR]) {
      styles.color = designOptions[ReportElementTextDesignOption.TEXT_COLOR];
    }

    if (designOptions[ReportElementTextDesignOption.FONT_FAMILY]) {
      styles['font-family'] = designOptions[ReportElementTextDesignOption.FONT_FAMILY];
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_ALIGNMENT]) {
      styles['text-align'] = designOptions[ReportElementTextDesignOption.TEXT_ALIGNMENT];
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_BOLD]) {
      styles['font-weight'] = FontWeight.BOLD;
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_ITALIC]) {
      styles['font-style'] = FontStyle.ITALIC;
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_UNDERLINE]) {
      styles['text-decoration'] = TextDecoration.UNDERLINE;
    }

    if (designOptions[ReportElementTextDesignOption.TEXT_STRIKETHROUGH]) {
      styles['text-decoration'] = styles['text-decoration']
        ? `${styles['text-decoration']} line-through`
        : 'line-through';
    }

    return styles;
  }

  static elementInnerStyle(element) {
    const baseOptions = element.metadata.base_options;
    const styles = {};
    styles['z-index'] = element.z_index;

    if (!baseOptions) {
      return styles;
    }

    if (baseOptions[ReportElementBaseOption.MIRROR_X]) {
      // eslint-disable-next-line tap/no-raw-text-js
      styles.transform = 'rotateX(180deg) ';
    }

    if (baseOptions[ReportElementBaseOption.MIRROR_Y]) {
      if (!styles.transform) {
        styles.transform = '';
      }
      // eslint-disable-next-line tap/no-raw-text-js
      styles.transform += 'rotateY(180deg) ';
    }

    if (baseOptions[ReportElementBaseOption.BACKGROUND_COLOR]) {
      styles['background-color'] = baseOptions[ReportElementBaseOption.BACKGROUND_COLOR];
    }

    if (baseOptions[ReportElementBaseOption.BORDER_RADIUS]) {
      styles['border-radius'] = `${baseOptions[ReportElementBaseOption.BORDER_RADIUS]}px`;
    }

    if (!element.isTypeShape()) {
      if (this.shouldApplyShadowColor(baseOptions)) {
        styles['box-shadow'] = `${baseOptions[ReportElementBaseOption.SHADOW_X]}px ${
          baseOptions[ReportElementBaseOption.SHADOW_Y]
        }px ${baseOptions[ReportElementBaseOption.SHADOW_BLUR]}px ${
          baseOptions[ReportElementBaseOption.SHADOW_COLOR]
        }`;
      }

      if (this.shouldApplyBorderColor(baseOptions)) {
        const color = changeColorOpacity(
          baseOptions[ReportElementBaseOption.BORDER_COLOR],
          baseOptions[ReportElementBaseOption.BORDER_OPACITY]
        );

        styles.border = `${color} ${baseOptions[ReportElementBaseOption.BORDER_WIDTH]}px ${
          baseOptions[ReportElementBaseOption.BORDER_TYPE]
        }`;
      }
    }

    if (baseOptions[ReportElementBaseOption.OPACITY] >= 0) {
      styles.opacity = baseOptions[ReportElementBaseOption.OPACITY] / 100;
    }

    return styles;
  }

  static elementInnerClass(element) {
    return element.metadata.base_options[ReportElementBaseOption.BORDER_TYPE]
      ? ['has-borders']
      : [];
  }

  static multiSelectClass(element, selectedElements) {
    const index = selectedElements.findIndex((el) => el.id === element.id);
    return index !== -1 ? ['state-multiple-selected'] : [];
  }

  static positionStyle(element, report) {
    const [left, top] = report.getElementCoordinates(element);
    const [width, height] = report.getElementDimensions(element);

    return {
      position: Position.ABSOLUTE,
      top,
      left,
      height,
      width,
      zIndex: element.z_index,
    };
  }

  static getShapeElementStyles(element) {
    const styles = {};

    if (element.metadata.design_options[ReportElementShapeDesignOptions.BORDER_RADIUS]) {
      styles['border-radius'] = `${
        element.metadata.design_options[ReportElementShapeDesignOptions.BORDER_RADIUS]
      }`;
    }

    // override border-radius
    if (element.isShapeSquare()) {
      styles['border-radius'] = `${
        element.metadata.base_options[ReportElementBaseOption.BORDER_RADIUS]
      }px`;
    }

    if (element.metadata.design_options[ReportElementShapeDesignOptions.BACKGROUND_COLOR]) {
      styles['background-color'] =
        element.metadata.design_options[ReportElementShapeDesignOptions.BACKGROUND_COLOR];
    }

    if (element.metadata.base_options.checkBorderOptions()) {
      const options = element.metadata.base_options;
      const color = changeColorOpacity(
        options[ReportElementBaseOption.BORDER_COLOR],
        options[ReportElementBaseOption.BORDER_OPACITY]
      );
      styles.border = `${color} ${options[ReportElementBaseOption.BORDER_WIDTH]}px ${
        options[ReportElementBaseOption.BORDER_TYPE]
      }`;
    }

    if (element.metadata.base_options.checkShadowOptions()) {
      const options = element.metadata.base_options;

      styles['box-shadow'] = `${options[ReportElementBaseOption.SHADOW_X]}px ${
        options[ReportElementBaseOption.SHADOW_Y]
      }px ${options[ReportElementBaseOption.SHADOW_BLUR]}px ${
        options[ReportElementBaseOption.SHADOW_COLOR]
      }`;
    }

    return styles;
  }

  static getImageElementClasses(element) {
    const classes = [];
    const fillMode = element.metadata.design_options[ReportElementImageDesignOptions.FILL_MODE];

    if (!fillMode) {
      return classes;
    }

    switch (fillMode) {
      case ReportElementImageSupportedFillMode.ASPECT_FIT:
        classes.push('aspect-fit');
        break;
      case ReportElementImageSupportedFillMode.ASPECT_FILL:
        classes.push('aspect-fill');
        break;
      case ReportElementImageSupportedFillMode.STRETCH:
        // eslint-disable-next-line tap/no-raw-text-js
        classes.push('stretch');
        break;
      default:
        classes.push('aspect-fit');
        break;
    }

    return classes;
  }

  static getIconElementStyles(report, element) {
    // todo:: According to legacy code have to write extra css styles for exporting
    const styles = {};
    let { height, width } = element;
    if (element.snap_to_grid) {
      [width, height] = report.getElementDimensions(element);
    }

    styles['font-size'] = `${Math.min(height, width) - 20}px`;

    if (element.metadata.design_options[ReportElementIconDesignOptions.ICON_COLOR]) {
      styles.color = element.metadata.design_options[ReportElementIconDesignOptions.ICON_COLOR];
    }

    return styles;
  }

  static getPercentToPixelValue(value, totalPixels) {
    return (value / 100) * totalPixels;
  }

  static getPixelToPercentValue(value, totalPixels) {
    return (value / totalPixels) * 100;
  }
}
